import type { FC } from 'react';
import { memo, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { resumeLowPriorityEvents } from '@confluence/analytics-event-delay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { browserMetricsState } from '../utils/index.ts';
import { reportHydrationError } from '../utils/reportHydrationError.ts';
import type { Props } from '../types/PageLoadEndProps.ts';

import { addCustomTimingForMetric } from './addCustomTimingsFromMarks.ts';

const UFOLoadEndComponent: FC<Props> = memo(({ metric, customData = {}, stopTime }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;

		if (metric) {
			addCustomTimingForMetric(metric.key);
		}

		addUFOCustomData({
			...customData,
			...(typeof stopTime === 'number' ? { stopTime } : {}),
			previousRoute: browserMetricsState.previousRoute,
			pageLoadInfo: getUniquePageLoadId(),
		});

		void resumeLowPriorityEvents();

		// Not adding exhaustive dependencies because we want this to fire only once,
		// despite any changes to `customData`
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [metric]);

	reportHydrationError(createAnalyticsEvent, metric?.key);

	return null;
});

export const UFOLoadEnd = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(UFOLoadEndComponent);
